import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { alpha, Box, Button, Typography } from "@mui/material";
import PropTypes from "prop-types";
import SideBarContractor from "../SideBar/SideBarContractor";

import "./Layout.css";
import { Suspense, useEffect, useMemo } from "react";
import LoadingOverlay from "../Custom/LoadingOverlay";
import MenuPopup from "./MenuPopup";
import useGroupMessaging from "../../customHooks/globalCustomHooks/useGroupMessaging";
import useOneOnOneMessaging from "../../customHooks/globalCustomHooks/useOneOnOneMessaging";
import useFetchUnreadMsgChatRoomsCount from "../../customHooks/globalCustomHooks/useFetchUnreadMsgChatRoomsCount";
import { getCssVariableHandler } from "../../utils/styles/getCssVariables";
import Calendar from "../../pages/contractors/calendar/Calendar";
import React, { useState } from "react";
import CalendarMiddleware from "../../pages/contractors/calendar/CalendarMiddleware";
import { ContractorConsent } from "./_components/ContractorConsent";
import useNoteOneOnOneSetupEvents from "../../customHooks/socketEvents/noteOneOnOneMessaging/useNoteOneOnOneSetupEvents";
import GustoLogo from "../../assets/images/gusto.png";
import { AlertDialogForSendOpportunity } from "../Custom/AlertDialog";
import { useDispatch, useSelector } from "react-redux";
import useNoteOneOnOneMessagingEvents from "../../customHooks/socketEvents/noteOneOnOneMessaging/useNoteOneOnOneMessagingEvents";
import { getEmailSanitized } from "../../utils/methods/formatters";
import { DocAndContracts } from "../../pages/contractors/components/AddContractorForms/_components/DocAndContracts";
import { alertDialogAciton, logoutAction } from "../../store/actions/auth";
import { hasAssignedContractorType } from "../../utils/methods/contractors/contractorUtils";
import { fetchOnboardingStatus } from "../../api/onboarding";

const LayoutContractor = () => {
  // static data start...
  const middlewareInit = React.useMemo(() => {
    return {
      state: null,
      type: "", // 'single' | 'multiple' | 'schedule'
    };
  }, []);
  const alertDataInit = useMemo(() => {
    return {
      status: false,
      title: "",
      message: "",
      onClose: () => {},
    };
  }, []);
  // static data end...

  // useState hooks start..
  const [showCal, setShowCal] = useState({
    state: false,
  });
  const [middleware, setMiddleware] = React.useState({
    state: null,
    type: "",
    holdAndPre: null,
  });
  const [alertData, setAlertData] = useState(alertDataInit);
  const { eventNotesAssignUser } = useSelector(
    (state) => state?.eventRequestDetails,
  );
  const { links } = useSelector((state) => state?.contractorLink);

  const { individualPosiitons } = useSelector(
    (state) => state?.contractorSchedules,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // useState hooks end..

  // custom hooks start...
  // group messaging custom hooks...
  useGroupMessaging();
  // group messaging custom hooks...

  // oneOnOne messaging custom hooks...
  useOneOnOneMessaging();
  // oneOnOne messaging custom hooks...

  // fetchUnreadMsgChatRoomsCount hooks...
  useFetchUnreadMsgChatRoomsCount();
  // fetchUnreadMsgChatRoomsCount hooks...

  // note socket event listener custom hook...
  useNoteOneOnOneMessagingEvents({
    roomIds: eventNotesAssignUser?.[0]?.chatId
      ? [eventNotesAssignUser?.[0]?.chatId]
      : [""],
    eventInfo: { _id: individualPosiitons?.data?.eventId },
  });
  //

  useNoteOneOnOneSetupEvents();
  // custom hooks end...

  const auth = useSelector((store) => store.auth);

  const isProfileComplete = useMemo(() => {
    return !!(
      auth?.contractorDetails?.mobile &&
      getEmailSanitized({ email: auth?.contractorDetails?.email }) &&
      auth?.contractorDetails?.mailingAddress &&
      auth?.contractorDetails?.city &&
      auth?.contractorDetails?.state &&
      auth?.contractorDetails?.zipCode &&
      auth?.contractorDetails?.dateOfBirth &&
      auth?.contractorDetails?.age &&
      auth?.contractorDetails?.emergencyContactName &&
      auth?.contractorDetails?.emergencyContactNumber
    );
  }, [auth]);

  const isProfileCompleted =
    auth?.contractorDetails?.contractorProfile?.isPersonalInformationAdded &&
    auth?.contractorDetails?.contractorProfile?.isRoleInformationAdded &&
    !!isProfileComplete;

  // Onboarding stage for the banner above the pages (Tiffaney, 2026-10-12): "complete"
  // means every task is done and the team only has to verify. Refreshed on every page
  // change and whenever the Onboarding page reports a change.
  const [onboardingStage, setOnboardingStage] = useState(null);
  // Third party companies are onboarded by the team: the banner says so instead of
  // asking them to finish contractor tasks (2026-10-15).
  const [onboardingThirdParty, setOnboardingThirdParty] = useState(false);
  useEffect(() => {
    if (auth?.isApproved || !auth?.userId) return undefined;
    let alive = true;
    const load = () => {
      fetchOnboardingStatus()
        .then((status) => {
          if (!alive) return;
          setOnboardingStage(status?.stage ?? null);
          setOnboardingThirdParty(status?.thirdParty === true);
        })
        .catch(() => {});
    };
    load();
    window.addEventListener("onboarding-status-changed", load);
    return () => {
      alive = false;
      window.removeEventListener("onboarding-status-changed", load);
    };
  }, [auth?.isApproved, auth?.userId, location.pathname]);

  // ConsentGate: bypassed while the contractor is still "invited" (no isApproved)
  // — they need to see and respond to the opportunity invitation before accepting
  // terms/SMS. Consent submission depends on the SMS service (deferred, see bug
  // #218 "Sms not found"). Once approved and still without consent, keep the gate.
  if (!auth?.termsAcceptedAt || !auth?.smsPermissionStatus) {
    if (auth?.isApproved) {
      return (
        <Box
          sx={{
            height: "50vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ContractorConsent />
        </Box>
      );
    }
    // contractor invited: keep rendering the app (sidebar+route),
    // disableDashboard still redirects to /opportunities/* via guards.
  }

  if (
    auth?.isApproved &&
    !hasAssignedContractorType(auth?.contractorType)
  ) {
    return (
      <Box
        sx={{
          minHeight: "50vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "1.5rem",
        }}
      >
        <Box
          sx={{
            maxWidth: "36rem",
            border: `2px solid ${getCssVariableHandler({
              variableName: "--primary-pink-hex",
            })}`,
            borderRadius: "0.7rem",
            background: alpha(
              getCssVariableHandler({ variableName: "--black-shade-2-rgb" }),
              0.5,
            ),
            padding: "1rem",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontWeight: 600, marginBottom: "0.35rem" }}>
            Contractor classification pending
          </Typography>
          <Typography>
            Your profile was verified, but your contractor type has not been
            assigned yet. Please contact the ShowPhaze team to complete your
            review.
          </Typography>
          {/* Without this the user is stuck here: the gate hides the sidebar logout. */}
          <Button
            className={"BtnBlackPink"}
            sx={{ marginTop: "1rem" }}
            onClick={() => {
              dispatch(logoutAction({}));
              navigate("/login");
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>
    );
  }

  // Documents for the worker type. Since the onboarding change (2026-10-08) the type is
  // set by the contractor's own answers before anyone verifies them, so the documents no
  // longer wait for `isApproved` (Verified): they are an onboarding step.
  if (
    !auth?.isAgreementAccepted &&
    ["V.C.", "w2", "1099"]?.includes(auth?.contractorType)
  ) {
    return <DocAndContracts contractorId={auth?.userId} />;
  }

  return (
    <Box sx={{}}>
      {/* NOTE: show notification to create an account on gusto */}

      {auth?.loading ? null : !auth?.isApproved || !isProfileCompleted ? (
        <Box
          sx={{
            zIndex: "2",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: { lg: "1.5rem" },
          }}
        >
          <Box
            sx={{
              border: `2px solid ${getCssVariableHandler({
                variableName: "--primary-pink-hex",
              })}`,
              textAlign: "center",
              width: "50%",
              "@media (max-width: 640px)": {
                width: "90%",
              },
              "@media (max-width: 900px)": {
                width: "75%",
              },
              padding: "0.5rem",
              borderRadius: "0.7rem",
              background: alpha(
                getCssVariableHandler({ variableName: "--black-shade-2-rgb" }),
                0.5,
              ),
            }}
          >
            {!auth?.isApproved ? (
              <>
                <Typography
                  sx={{
                    color: getCssVariableHandler({
                      variableName: "--red-shade-1rgb",
                    }),
                    fontWeight: "500",
                  }}
                >
                  {onboardingThirdParty && onboardingStage !== "complete"
                    ? "Thanks. The ShowPhaze team is finishing your company setup and will reach out if anything is needed."
                    : onboardingStage === "complete"
                    ? "Your onboarding is complete and we are waiting on verification by our team. Thank you."
                    : onboardingStage === "profile_only"
                      ? "Your profile is saved. ShowPhaze will reach out when work is available in your area."
                      : location.pathname === "/onboarding"
                        ? "Your onboarding isn't finished. Please complete all the tasks below."
                        : "Your onboarding isn't finished. Please complete all tasks by clicking Onboard."}
                </Typography>
                {location.pathname !== "/onboarding" &&
                onboardingStage !== "complete" &&
                !onboardingThirdParty ? (
                  <Button
                    className={"BtnBlackPink"}
                    sx={{ marginTop: "0.5rem" }}
                    onClick={() => navigate("/onboarding")}
                  >
                    Onboard
                  </Button>
                ) : null}
              </>
            ) : (
              <Typography
                sx={{
                  color: getCssVariableHandler({
                    variableName: "--red-shade-1rgb",
                  }),
                  fontWeight: "500",
                }}
              >
                Your onboarding is incomplete. Please provide the required
                information as soon as possible to prevent any impact to your
                work, bookings, and/or payment delays. Click on edit profile to
                complete.
              </Typography>
            )}
          </Box>
        </Box>
      ) : null}

      {/* <NavBarClient /> */}
      <Box
        sx={{
          position: "relative",
          padding: "40px",
          display: "flex",
          gap: "16px",
          alignItems: "flex-start",
          "@media (max-width: 1200px)": {
            flexDirection: "column",
            paddingTop: "20px",
            "& > div:first-of-type": {
              display: "none",
            },
          },
          "@media (max-width: 600px)": {
            padding: "15px",
          },
        }}
      >
        <SideBarContractor />
        <MenuPopup
          sx={{
            "@media (max-width: 650px)": {
              marginTop: "3.25rem",
            },
          }}
        >
          <SideBarContractor />
        </MenuPopup>

        <Box className="top-right-button-container">
          {/* The old "Workmarket Onboarding" button was removed on 2026-10-12 (Tiffaney): the
              WorkMarket ID is now a step of the Onboarding page, with its own help. */}

          {!auth?.disableDashboard && (
            <Button
              sx={{
                "@media (max-width: 650px)": {
                  flex: "1",
                },
              }}
              onClick={() => setShowCal((prev) => ({ ...prev, state: true }))}
              className={"BtnBlackPink"}
            >
              My Calendar
            </Button>
          )}
        </Box>
        <Box
          data-route-scroll-root
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            flex: "1",
            width: "100%",
            borderRadius: "16px",
            padding: "0",
            border: "2px solid #D175B6",
            background: "rgba(0, 0, 0, 0.50)",
            // background: "red",
            minHeight: "calc(100vh - 204px)",
            // minWidth: "calc(100vw - 396px)",
            overflow: "scroll",
            "&::-webkit-scrollbar": {
              width: 0, // Hide scrollbar on WebKit browsers (e.g., Chrome, Safari)
            },
            scrollbarWidth: "none", // Hide scrollbar on Firefox
          }}
        >
          <Suspense fallback={<LoadingOverlay open={true} />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
      <Calendar
        open={showCal?.state}
        handleClose={() => setShowCal((prev) => ({ ...prev, state: false }))}
        setMiddleware={setMiddleware}
      />

      {middleware?.state ? (
        <CalendarMiddleware
          data={middleware}
          holdAndPre={middleware?.holdAndPre}
          handleClose={(all = true) => {
            setMiddleware((middleware) => {
              return {
                state: null,
              };
            });
            if (all) {
              setShowCal((prev) => ({ ...prev, state: false }));
            }
          }}
        />
      ) : null}
      <AlertDialogForSendOpportunity
        open={alertData?.status}
        title={alertData?.title}
        message={alertData?.message}
        handleClose={alertData?.onClose}
        isSmallWidth
      />
    </Box>
  );
};

export default LayoutContractor;

LayoutContractor.propTypes = {
  children: PropTypes.node,
};
